import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { BEHAVIOR_PLAN_VERSION } from '@erp/shared-types';
import { orpc, rpcClient } from 'src/api/rpc/client';

/** Hooks over ERP Apps (`apps.*`). */

/** The organization's Apps. Pass `enabled: false` for a viewer who can't read them. */
export function useApps(options?: { enabled?: boolean }) {
  return useQuery({
    ...orpc.apps.list.queryOptions({ input: {} }),
    staleTime: 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * One App with its versions and recent tool-call repairs. Pass `enabled`
 * from the open so repairs the open just recorded are included.
 */
export function useAppDetails(appId: string, options?: { enabled?: boolean }) {
  return useQuery({
    ...orpc.apps.get.queryOptions({ input: { appId } }),
    staleTime: 30 * 1000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Open an App: runs its tool feeds server-side. Not cached across visits, so
 * every open reflects current data and the current version.
 */
export function useOpenApp(appId: string) {
  return useQuery({
    ...orpc.apps.open.queryOptions({
      input: {
        appId,
        // A v2 App refuses a client that cannot run its plan.
        supportedBehaviorPlanVersion: BEHAVIOR_PLAN_VERSION,
      },
    }),
    staleTime: 0,
    gcTime: 0,
    // Opening runs the repair loop (repairs, notices, maybe a deploy) and a
    // refetch remounts the view, dropping input: open once, on purpose.
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useRunAppCommand(appId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: (input: {
      command: string;
      params: Record<string, unknown>;
      deploymentId: string;
    }) => rpcClient.apps.runCommand({ appId, ...input }),
    onSettled: () => {
      // A failed call may have saved a repair or deployed a fix.
      void queryClient.invalidateQueries({
        queryKey: orpc.apps.get.key({ input: { appId } }),
      });
    },
  });
}

export function useSetAppAutoUpdate(appId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      rpcClient.apps.setAutoUpdate({ appId, enabled }),
    onSuccess: () => {
      // Not `apps.open`: refetching it would remount the view and drop input.
      void queryClient.invalidateQueries({ queryKey: orpc.apps.list.key() });
      void queryClient.invalidateQueries({
        queryKey: orpc.apps.get.key({ input: { appId } }),
      });
    },
  });
}

/**
 * Open a Report with the filter inputs it starts from: reads its feeds as the
 * viewer. Opened once; new inputs go through `useReportFeeds`, which patches
 * the view's state instead of remounting it.
 */
export function useOpenReport(appId: string, inputs: Record<string, unknown>) {
  return useQuery({
    ...orpc.apps.openReport.queryOptions({ input: { appId, inputs } }),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/** A Report's feeds read again with new inputs; the newest inputs win. */
export function useReportFeeds(input: {
  appId: string;
  deploymentId: string;
  inputs: Record<string, unknown>;
  enabled: boolean;
}) {
  const { enabled, ...request } = input;
  return useQuery({
    ...orpc.apps.readReport.queryOptions({ input: request }),
    enabled,
    // The view keeps the last feeds while the next inputs are read.
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
